import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/layout/Layout";

import Home from "@/pages/Home";
import Solutions from "@/pages/Solutions";
import Plans from "@/pages/Plans";
import HowItWorks from "@/pages/HowItWorks";
import Industries from "@/pages/Industries";
import IndustryDetail from "@/pages/IndustryDetail";
import About from "@/pages/About";
import Results from "@/pages/Results";
import Insights from "@/pages/Insights";
import Article from "@/pages/Article";
import Contact from "@/pages/Contact";
import ForTradies from "@/pages/ForTradies";
import CampaignLanding from "@/pages/CampaignLanding";
import EnergyGrowthPilot from "@/pages/EnergyGrowthPilot";
import InstallerGrowthPilot from "@/pages/InstallerGrowthPilot";
import Discovery from "@/pages/Discovery";
import DiscoveryChooser from "@/pages/DiscoveryChooser";
import DiscoveryThankYou from "@/pages/DiscoveryThankYou";
import ThankYou from "@/pages/ThankYou";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import NotFound from "@/pages/NotFound";

import { AuthProvider, useAuth, userModules } from "@/admin/AuthContext";
import AdminLogin from "@/admin/AdminLogin";
import AdminLayout from "@/admin/AdminLayout";
import AdminDashboard from "@/admin/AdminDashboard";
import LeadsList from "@/admin/LeadsList";
import LeadDetail from "@/admin/LeadDetail";
import ClientsList from "@/admin/ClientsList";
import ClientDetail from "@/admin/ClientDetail";
import ServicesList from "@/admin/ServicesList";
import UsersList from "@/admin/UsersList";
import TasksPage from "@/admin/TasksPage";
import ReportsPage from "@/admin/ReportsPage";
import SmsPage from "@/admin/SmsPage";
import OutreachPage from "@/admin/OutreachPage";
import OnboardingList from "@/admin/OnboardingList";
import OnboardingDetail from "@/admin/OnboardingDetail";
import SetPassword from "@/admin/SetPassword";
import OnboardingPage from "@/pages/onboarding/OnboardingPage";

function ModuleRoute({ modules, roles, children }) {
  const { user, loading } = useAuth();
  if (loading || !user) return null;
  if (roles && !roles.includes(user.role)) return <Navigate to="/admin" replace />;
  if (modules && !modules.some((m) => userModules(user).includes(m)))
    return <Navigate to="/admin" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Admin (no public layout) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/set-password" element={<SetPassword />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="leads" element={<LeadsList />} />
            <Route path="leads/:id" element={<LeadDetail />} />
            <Route path="clients" element={<ModuleRoute modules={["clients"]}><ClientsList /></ModuleRoute>} />
            <Route path="clients/:id" element={<ModuleRoute modules={["clients"]}><ClientDetail /></ModuleRoute>} />
            <Route path="services" element={<ModuleRoute modules={["services"]}><ServicesList /></ModuleRoute>} />
            <Route path="tasks" element={<ModuleRoute modules={["leads", "clients"]}><TasksPage /></ModuleRoute>} />
            <Route path="sms" element={<ModuleRoute modules={["sms"]}><SmsPage /></ModuleRoute>} />
            <Route path="outreach" element={<ModuleRoute modules={["outreach"]}><OutreachPage /></ModuleRoute>} />
            <Route path="installer-growth-pilot/onboarding" element={<ModuleRoute roles={["admin", "manager"]}><OnboardingList /></ModuleRoute>} />
            <Route path="installer-growth-pilot/onboarding/:id" element={<ModuleRoute roles={["admin", "manager"]}><OnboardingDetail /></ModuleRoute>} />
            <Route path="reports" element={<ModuleRoute roles={["admin", "manager"]}><ReportsPage /></ModuleRoute>} />
            <Route path="users" element={<ModuleRoute modules={["users"]}><UsersList /></ModuleRoute>} />
          </Route>

          {/* Public onboarding — own slim header, outside the marketing Layout */}
          <Route path="/installer-growth-pilot/onboarding" element={<OnboardingPage />} />

          <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/industries/:slug" element={<IndustryDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/results" element={<Results />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<Article />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/for" element={<ForTradies />} />
          <Route path="/for/installer-growth-pilot" element={<InstallerGrowthPilot />} />
          <Route path="/for/:slug" element={<CampaignLanding />} />
          <Route path="/energy-growth-pilot" element={<EnergyGrowthPilot />} />
          <Route path="/discovery" element={<DiscoveryChooser />} />
          <Route path="/discovery/thank-you" element={<DiscoveryThankYou />} />
          <Route
            path="/discovery/energy-growth-pilot"
            element={<Discovery pilot="energy-growth-pilot" />}
          />
          <Route
            path="/discovery/installer-growth-pilot"
            element={<Discovery pilot="installer-growth-pilot" />}
          />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        </Routes>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
